import { FilterableDocumentsContext } from '@/contexts/FilterableDocumentsContext';
import { Body5, Container } from '@ui';
import React from 'react';
import styled from 'styled-components';
import { DocTypeSelectBar } from '../components/DocTypeSelectBar';
import { ReportingPeriodSelectBar } from '../components/ReportingPeriodSelectBar';
import { SelectableDocumentsList } from '@containers/documents/SelectableDocumentsList';
import { FormStateContext } from '@/contexts/FormStateContext';
import { filterDuplicateCompanies, filterDuplicateDocuments } from '@utils/usefulFuncs';
import { TagSelectBar } from '../components/TagSelectBar';
import { Working } from '@ui/Working';

const Wrapper = styled.div``;
const FilterBarsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FILTERED_DOCUMENT_SELECTION_FILTERS = {
  tags: 'tags',
  documentTypes: 'documentTypes',
  reportingPeriods: 'reportingPeriods',
};

export const FilteredDocumentsSelection = ({ singleDocMode = false, filterModes = [], initializing }) => {
  const { loadingDocuments, filteredDocuments, documentTypes, reportingPeriodTypes } =
    React.useContext(FilterableDocumentsContext);
  const { state: formState, setFormStateValue } = React.useContext(FormStateContext);
  const { selectedDocs: value } = formState;

  const onChange = (value) => {
    setFormStateValue('selectedDocs', value);
  };

  const onAddTag = (tag) => {
    setFormStateValue('tags', filterDuplicateCompanies([...formState.tags, tag]));
  };

  const onRemoveDocument = (document) => {
    if (Array.isArray(document)) {
      const removeDocIds = document.map((d) => d.id);
      onChange(value.filter((d) => !removeDocIds.includes(d.id)));
    } else {
      onChange(value.filter((d) => d.id !== document.id));
    }
  };

  const onAddDocument = (document) => {
    if (singleDocMode) {
      const docs = Array.isArray(document) ? document : [document];
      const lastDoc = docs[docs.length - 1];
      onChange(lastDoc ? [lastDoc] : []);
      return;
    }
    if (Array.isArray(document)) {
      onChange(filterDuplicateDocuments([...value, ...document]));
    } else {
      onChange(filterDuplicateDocuments([...value, document]));
    }
  };

  // Before any company/tag has been searched there are intentionally no documents to
  // show, so prompt the user to search rather than render "No documents found".
  const showSearchPrompt =
    filterModes.includes(FILTERED_DOCUMENT_SELECTION_FILTERS.tags) && formState.tags.length === 0;

  if (initializing) {
    return <Working />;
  }

  return (
    <Wrapper>
      <FilterBarsWrapper>
        {filterModes.includes(FILTERED_DOCUMENT_SELECTION_FILTERS.tags) && (
          <TagSelectBar tags={formState.tags} onAddTag={onAddTag} />
        )}
        {filterModes.includes(FILTERED_DOCUMENT_SELECTION_FILTERS.documentTypes) && (
          <DocTypeSelectBar documentTypes={documentTypes} />
        )}
        {filterModes.includes(FILTERED_DOCUMENT_SELECTION_FILTERS.reportingPeriods) && (
          <ReportingPeriodSelectBar reportingPeriodTypes={reportingPeriodTypes} />
        )}
      </FilterBarsWrapper>

      <Container top={16}>
        {loadingDocuments ? (
          <Working />
        ) : showSearchPrompt ? (
          <Container top={48} centerAll>
            <Body5 isLowContrast>Search a company or tag above and the results will be shown here.</Body5>
          </Container>
        ) : (
          <SelectableDocumentsList
            docs={filteredDocuments}
            selectedDocs={value}
            onSelect={onAddDocument}
            onRemoveSelect={onRemoveDocument}
            singleDocMode={singleDocMode}
          />
        )}
      </Container>
      <Container top={100} />
    </Wrapper>
  );
};
